import { useEffect, useState } from "react"
import { getDatabase, ref, onValue, set as dbSet, } from "firebase/database"
import { getAuth } from "firebase/auth"
import { onAuthStateChanged } from "firebase/auth"
import { Link } from "react-router-dom"
import { Info, ShoppingCart, Filter, Search } from 'lucide-react'
import "./NewProducts.css"
import { FaInfoCircle } from "react-icons/fa"

const NewProducts = () => {
  const [acceptedProducts, setAcceptedProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [expandedProductId, setExpandedProduct] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedImage, setSelectedImage] = useState(null)
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [productNames, setProductNames] = useState([])
  const [selectedProductName, setSelectedProductName] = useState("all")


  const toggleDetails = (productId) => {
    setExpandedProduct((prev) => (prev === productId ? null : productId));
  };

  const auth = getAuth()
  const user = auth.currentUser


  useEffect(() => {
    const dbInstance = getDatabase();
    const connectedRef = ref(dbInstance, ".info/connected");

    // Check offline
    if (!navigator.onLine) {
      setErrorMessage("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setErrorMessage("Session expired. Please sign in.");
        setLoading(false);
        return;
      }
      setErrorMessage("");
      const productsRef = ref(dbInstance, "newProducts");

      // Check Firebase connection
      onValue(connectedRef, (snap) => {
        if (snap.val() === false) {
          setErrorMessage("Session expired please login again.");
          setLoading(false);
          return;
        }
        setErrorMessage("");
        const unsubscribeProducts = onValue(productsRef, (snapshot) => {
          if (snapshot.exists()) {
            const rawData = snapshot.val();
            const productsList = [];

            Object.entries(rawData).forEach(([outerId, productObj]) => {
              Object.entries(productObj).forEach(([offerId, productData]) => {
                const product = { id: outerId, offerId, ...productData };
                productsList.push(product);
              });
            });

            setAcceptedProducts(productsList);

            const names = Array.from(new Set(productsList.map((p) => p.name).filter(Boolean)));
            setProductNames(names);
          } else {
            setAcceptedProducts([]);
            setProductNames([]);
          }

          setLoading(false);
        });

        return () => unsubscribeProducts();
      });
    });

    return () => unsubscribeAuth();
  }, []);

  // Filter accepted products by category and product name
  const filteredAcceptedProducts = acceptedProducts.filter((product) => {
    // Filter by category
    const matchesCategory =
      selectedCategory === "all" ||
      (product.category && product.category.toLowerCase() === selectedCategory.toLowerCase())

    // Filter by product name
    const matchesName = selectedProductName === "all" || (product.name && product.name === selectedProductName)

    // Filter by search term
    const matchesSearch = !searchTerm || (product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase()))

    return matchesCategory && matchesName && matchesSearch
  })

  // Get unique categories from accepted products
  const categories = [
    { id: "all", name: "All Categories" },
    ...Array.from(new Set(acceptedProducts.map((product) => product.category).filter(Boolean))).map((category) => ({
      id: category.toLowerCase(),
      name: category.charAt(0).toUpperCase() + category.slice(1),
    })),
  ]

  // Function to handle image click and show larger version
  const handleImageClick = (product) => {
    setSelectedImage(product)
  }

  // Function to close the image modal
  const closeImageModal = () => {
    setSelectedImage(null)
  }

  // Toggle filter menu
  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu)
  }
  const productAlarm = () => {
    alert(
      "Welcome to the New Products page!\n\n" +
      "Here you can:\n" +
      "- Browse the latest products available for order.\n" +
      "- Use the search bar to quickly find specific products.\n" +
      "- Filter products by category and name to narrow down your options.\n" +
      "- View  order list by clicking the 'View Order List' button.\n" +
      "- Click 'Details' to see more information about each product.\n\n" +
      "Please ensure you are logged in to add products to your order list.\n" +
      "For any questions or support, feel free to reach out!"
    );
  }

  return (
    <div className="product-catalog-container">
      <div className="product-catalog-header">
        <h1 className=" newpro-text">New Products {" "}
          <FaInfoCircle style={{ cursor: 'pointer', fontSize: '1.2rem', color: '#EA8716' }} onClick={productAlarm} />
        </h1>
        <div className="product-search-filter-container">
          <div className="product-search-container">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="product-catalog-actions">
            <div className="filter-dropdown">
              <button className="filter-btn" onClick={toggleFilterMenu}>
                <Filter size={18} />
                <span>Filter</span>
              </button>
              {showFilterMenu && (
                <div className="filter-menu">
                  <div className="filter-section">
                    <h4>Products</h4>
                    <button
                      className={`filter-option ${selectedProductName === "all" ? "active" : ""}`}
                      onClick={() => {
                        setSelectedProductName("all")
                        setShowFilterMenu(false)
                      }}
                    >
                      All Products
                    </button>
                    {productNames.map((name) => (
                      <button
                        key={name}
                        className={`filter-option ${selectedProductName === name ? "active" : ""}`}
                        onClick={() => {
                          setSelectedProductName(name)
                          setShowFilterMenu(false)
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link to="/dashboard/product-order" className="view-cart-btn">
              <ShoppingCart size={18} />
              <span>View Order List</span>
            </Link>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loader"></div>
          <span>Loading products...</span>
        </div>
      )}

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {!loading && acceptedProducts.length === 0 && (
        <div className="empty-state">
          <p>No products found.</p>
        </div>
      )}

      {/* Active filters display */}
      {(selectedCategory !== "all" || selectedProductName !== "all" || searchTerm) && (
        <div className="active-filters">
          <span>Active Filters:</span>
          {selectedCategory !== "all" && (
            <div className="filter-tag">
              Category: {categories.find((c) => c.id === selectedCategory)?.name}
              <button onClick={() => setSelectedCategory("all")} className="remove-filter">
                ×
              </button>
            </div>
          )}
          {selectedProductName !== "all" && (
            <div className="filter-tag">
              Product: {selectedProductName}
              <button onClick={() => setSelectedProductName("all")} className="remove-filter">
                ×
              </button>
            </div>
          )}
          {searchTerm && (
            <div className="filter-tag">
              Search: {searchTerm}
              <button onClick={() => setSearchTerm("")} className="remove-filter">
                ×
              </button>
            </div>
          )}
          <button
            onClick={() => {
              setSelectedCategory("all")
              setSelectedProductName("all")
              setSearchTerm("")
            }}
            className="clear-all-filters"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Grid layout for accepted products */}
      {!loading && filteredAcceptedProducts.length === 0 ? (
        <div className="empty-state">
          <p>No products match your filter criteria.</p>
          <button
            onClick={() => {
              setSelectedCategory("all")
              setSelectedProductName("all")
              setSearchTerm("")
            }}
            className="reset-filters-btn"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        filteredAcceptedProducts.length > 0 && (
          <div className="product-grids">
            {filteredAcceptedProducts.map((product) => {
              const isExpanded = expandedProductId === `${product.id}-${product.offerId}`;
              return (
                <div
                  key={`${product.id}-${product.offerId}`}
                  className={`product-Cards ${isExpanded ? "expanded" : ""}`}
                >
                  {isExpanded ? (
                    <div className="product-info expanded-view">
                      <button className="close-details-btn" onClick={() => setExpandedProduct(null)}>
                        × Close
                      </button>
                      <h3 className="product-title">{product.name}</h3>
                      <p className="product-company">
                        <strong>Company:</strong> {product.brandName}
                      </p>
                      <p className="product-description">{product.description}</p>
                      {product.productDetails && (
                        <div className="product-specs">
                          <h4>Specifications</h4>
                          <p>{product.productDetails}</p>
                        </div>
                      )}
                      <div className="product-actions">
                        <Link to="/dashboard/quote-request" className="quote-btn">
                          Get Quote
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="product-image-container">
                        <img
                          src={product.fileUrl || "/placeholder.svg"}
                          alt={product.name}
                          className="product-image"
                          onClick={() => handleImageClick(product)}
                        />
                      </div>
                      <div className="product-info">
                        <h3 className="product-title">{product.name}</h3>
                        {product.category && (
                          <span className="product-category-badge">{product.category}</span>
                        )}
                        <p className="product-company">
                          <span>Company:</span> {product.brandName}
                        </p>
                        <div className="product-actions">
                          <button
                            className="details-btn"
                            onClick={() =>
                              setExpandedProduct(`${product.id}-${product.offerId}`)
                            }
                          >
                            <Info size={16} />
                            <span>Details</span>
                          </button>
                          <Link to="/dashboard/quote-request" className="quote-btn">
                            Get Quote
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {selectedImage && (
        <div className="image-modal" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-modal" onClick={closeImageModal}>
              &times;
            </span>
            <img
              src={selectedImage.fileUrl || "/placeholder.svg"}
              alt={selectedImage.name}
              className="modal-image"
            />
            <h3 className="modal-title">{selectedImage.name}</h3>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewProducts;